<script lang="ts">
  import { afterNavigate, goto } from "$app/navigation";
  import { page } from "$app/stores";
  import { get } from "svelte/store";
  import Icons from "$lib/main/components/Icons.svelte";
  import { project } from "$lib/services/store";
  import AuthProvider from "$lib/main/AuthProvider.svelte";
  import { browser } from "$app/environment";
  import HamburgerMenu from "virtual:icons/mdi/hamburger-menu";
  import CloseMenu from "virtual:icons/mdi/close";
  import NavItem from "./NavItem.svelte";
  import UserMenu from "./UserMenu.svelte";
  import MeilisearchSearch from "$lib/admin/components/blocks/helpers/MeilisearchSearch.svelte";

  let innerWidth = 0;
  let mobileMenuOpen = false;
  let dropdownStates: boolean[] = [];

  function handleLogoClick(event: MouseEvent) {
    event.preventDefault();
    const currentPath = get(page).url.pathname;

    if (currentPath === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.location.href = "/";
    }
  }

  function toggleDropdown(index: number, forceState?: boolean) {
    if (browser) {
      if (innerWidth < 768) {
        if (forceState === true || (forceState === undefined && !dropdownStates[index])) {
          dropdownStates = Array(menuItems.length).fill(false);
        }
      }
    }

    if (forceState !== undefined) {
      dropdownStates[index] = forceState;
    } else {
      dropdownStates[index] = !dropdownStates[index];
    }
    dropdownStates = [...dropdownStates];
  }

  // Reactive declarations
  $: menuItems = $project?.menu?.menu ?? [];
  $: dropdownStates = Array(menuItems.length).fill(false);
  $: activeUrl = $page.url.pathname;
  $: isAuth0Enabled = $project.settings?.auth0?.enabled ?? false;

  function toggleMobileMenu() {
    mobileMenuOpen = !mobileMenuOpen;
    dropdownStates = Array(menuItems.length).fill(false);

    if (browser) {
      if (mobileMenuOpen) {
        document.body.classList.add("overflow-hidden");
      } else {
        document.body.classList.remove("overflow-hidden");
      }
    }
  }

  afterNavigate(() => {
    mobileMenuOpen = false;

    if (browser) {
      document.body.classList.remove("overflow-hidden");
    }
  });
</script>

<svelte:window bind:innerWidth />

<AuthProvider>
  <header class="content-grid bg-brand sticky top-0 z-50">
    <div class="flex flex-wrap justify-between items-center full-width">
      <!-- Mobile menu button -->
      <div class="flex justify-between items-center py-3 w-full">
        <button
          type="button"
          class="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-white hover:bg-brand-light md:hidden"
          aria-controls="mobile-menu"
          aria-expanded={mobileMenuOpen}
          on:click={toggleMobileMenu}>
          <span class="sr-only">Open main menu</span>
          <!-- Icon when menu is closed -->
          <HamburgerMenu class={mobileMenuOpen ? "hidden" : "block h-6 w-6 m-0"} />
          <!-- Icon when menu is open -->
          <CloseMenu class={mobileMenuOpen ? "block h-6 w-6 m-0" : "hidden"} />
        </button>
        <!-- Logo -->
        <a href="/" class="flex items-center" on:click={handleLogoClick}>
          <img
            class="w-[200px] h-8"
            alt={$project.name + " Logo"}
            src={$project.data?.imageFolders?.assets + $project.name_lower + "-logo.svg?w=200&q=100&auto=format"} />
        </a>
        <!-- Right side utilities -->
        <div class="flex gap-4 justify-center items-center">
          {#if $project.menu?.search == "active"}
            <div class="flex">
              <MeilisearchSearch />
            </div>
          {/if}
          <UserMenu isMobile={false} />
        </div>
      </div>
    </div>

    <nav aria-label="Main navigation" class="flex flex-grow full-width bg-brand-lighter md:h-[60px] relative">
      <!-- Mobile menu overlay when open -->
      <button
        type="button"
        class="{mobileMenuOpen
          ? 'opacity-50 absolute top-0 w-screen h-screen'
          : 'opacity-0 pointer-events-none top-0 absolute'} fixed inset-0 bg-black transition-opacity duration-300 ease-in-out z-2 md:hidden border-0"
        aria-label="Close mobile menu"
        on:click={() => toggleMobileMenu()}
        on:keydown={(e) => e.key === "Escape" && toggleMobileMenu()}></button>

      <!-- Navigation menu with slide animation -->
      <div
        class="{mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
                   absolute h-screen transition-all duration-300 ease-in-out md:static top-0 left-0 w-4/5 max-w-xs
                   bg-brand-lighter z-4 pb-20 overflow-y-auto
                   md:translate-x-0 md:h-auto md:w-full md:max-w-none md:overflow-visible md:pb-0 md:transition-none full-width md:col-[content] md:block">
        <!-- Main navigation links -->
        <ul class="flex flex-col p-4 space-y-2 md:h-full md:flex md:flex-row md:space-y-0 md:p-0">
          {#if $project.menu?.menu}
            {#each menuItems as navitem, index}
              <NavItem {navitem} {index} isActive={activeUrl === navitem.path} {dropdownStates} {toggleDropdown} />
            {/each}
          {/if}
        </ul>

        <!-- Mobile user menu -->
        <UserMenu isMobile={true} />

        <!-- Mobile-only social icons - inside sliding panel -->
        <div class="flex justify-center mt-8 space-x-4 md:hidden">
          {#each $project.menu.footerMenu.socials as social}
            <a
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-opacity-80 hover:bg-opacity-100 {social.platform ===
              'facebook'
                ? 'bg-[#37589b]'
                : social.platform === 'twitter'
                  ? 'bg-[#31abe1]'
                  : social.platform === 'instagram'
                    ? 'bg-gradient-to-r from-[#405de6] via-[#833ab4] to-[#fd1d1d]'
                    : social.platform === 'youtube'
                      ? 'bg-[#ff0000]'
                      : social.platform === 'gmail'
                        ? 'bg-white'
                        : social.platform === 'linkedin'
                          ? 'bg-[#0077b5]'
                          : 'bg-gray-500'}">
              <Icons name={social.platform} strokeWidth="1.5" width="20px" height="20px" color="white" />
            </a>
          {/each}
        </div>
      </div>
    </nav>
  </header>
</AuthProvider>
